import { useParams, Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);
  const relatedArticles = useMemo(
    () =>
      article
        ? articles.filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id).slice(0, 3)
        : [],
    [article]
  );

  useEffect(() => {
    if (article) {
      document.title = `${article.title} - 17黑料网`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', article.excerpt);
      }
    }
  }, [article]);

  if (!article) {
    return (
      <main>
        <Navbar />
        <div className="min-h-[60vh] flex items-center justify-center bg-background-50 pt-16">
          <p className="text-foreground-500">文章不存在</p>
        </div>
        <Footer />
      </main>
    );
  }

  const renderContent = (content: string) => {
    const lines = content.split('\n');
    const elements: JSX.Element[] = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      if (line.startsWith('## ')) {
        elements.push(
          <h2 key={i} className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
            {line.replace('## ', '')}
          </h2>
        );
        i++;
      } else if (line.startsWith('> ')) {
        elements.push(
          <blockquote key={i} className="border-l-4 border-primary-500 pl-5 py-2 my-6 bg-primary-50/50 rounded-r-lg">
            <p className="text-foreground-700 italic leading-relaxed">{line.replace('> ', '')}</p>
          </blockquote>
        );
        i++;
      } else if (line.match(/^\d+\./)) {
        const listItems: string[] = [];
        while (i < lines.length && (lines[i].match(/^\d+\./) || lines[i].startsWith('- '))) {
          listItems.push(lines[i].replace(/^\d+\.\s*/, '').replace(/^- /, ''));
          i++;
        }
        elements.push(
          <ol key={i} className="list-decimal list-inside space-y-2 my-4 text-foreground-700">
            {listItems.map((item, idx) => (
              <li key={idx} className="leading-relaxed">{item}</li>
            ))}
          </ol>
        );
      } else if (line === '') {
        i++;
      } else {
        const paragraphLines: string[] = [];
        while (i < lines.length && lines[i] !== '' && !lines[i].startsWith('## ') && !lines[i].startsWith('> ') && !lines[i].match(/^\d+\./)) {
          paragraphLines.push(lines[i]);
          i++;
        }
        elements.push(
          <p key={i} className="text-foreground-700 leading-relaxed mb-4">
            {paragraphLines.join(' ')}
          </p>
        );
      }
    }

    return elements;
  };

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '17黑料网',
      url: 'http://yiyaxiang.cn/',
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://yiyaxiang.cn/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.categoryName,
        item: `http://yiyaxiang.cn/category/${article.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <main>
      <Navbar />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />

      <article className="pt-16 bg-background-50">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-12">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />
        </div>

        <section className="relative w-full h-[360px] md:h-[480px] overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.title}
            title={`${article.title} - 17黑料网`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>
        </section>

        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-12 -mt-16 relative z-10">
          <div className="bg-background-50 rounded-t-2xl pt-8 md:pt-12">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-3 py-1 text-xs bg-primary-100 text-primary-700 rounded-full font-label transition-colors hover:bg-primary-200"
              >
                {article.categoryName}
              </Link>
              {article.isExclusive && (
                <span className="px-3 py-1 text-xs bg-accent-100 text-accent-800 rounded-full font-label">
                  独家首发
                </span>
              )}
              {article.isHot && (
                <span className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-full font-label">
                  热门
                </span>
              )}
            </div>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-black text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>

            <p className="text-base md:text-lg text-foreground-500 leading-relaxed mb-6">
              {article.excerpt}
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10 p-5 rounded-xl bg-background-100">
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-foreground-900 font-label">
                  {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万+` : `${article.views}+`}
                </p>
                <p className="text-xs text-foreground-500 mt-1">阅读量</p>
              </div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-foreground-900 font-label">
                  {article.comments >= 1000 ? `${(article.comments / 1000).toFixed(1)}千+` : `${article.comments}+`}
                </p>
                <p className="text-xs text-foreground-500 mt-1">评论数</p>
              </div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-foreground-900 font-label">
                  {article.readTime}分钟
                </p>
                <p className="text-xs text-foreground-500 mt-1">阅读时长</p>
              </div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-bold text-foreground-900 font-label">
                  {article.date}
                </p>
                <p className="text-xs text-foreground-500 mt-1">发布日期</p>
              </div>
            </div>

            <div className="prose-custom">
              {renderContent(article.content)}
            </div>

            <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-background-200">
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 text-xs bg-background-100 text-foreground-600 rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="flex items-center gap-4 mt-8 p-5 rounded-xl bg-background-100">
              <span className="text-sm text-foreground-500">分享到：</span>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-100 text-foreground-500 hover:text-primary-600 transition-colors" aria-label="分享到微博">
                <i className="ri-weibo-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-100 text-foreground-500 hover:text-primary-600 transition-colors" aria-label="分享到微信">
                <i className="ri-wechat-line"></i>
              </button>
              <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-100 text-foreground-500 hover:text-primary-600 transition-colors" aria-label="复制链接">
                <i className="ri-link"></i>
              </button>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="mt-16">
              <h3 className="font-heading text-xl font-bold text-foreground-950 mb-6">
                相关文章
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.id} {...ra} variant="default" />
                ))}
              </div>
            </div>
          )}

          <div className="mt-16 mb-10 p-8 rounded-2xl bg-primary-500/10 border border-primary-200 text-center">
            <h3 className="font-heading text-lg font-bold text-primary-700 mb-2">
              持续关注 · 最新进展
            </h3>
            <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto">
              不错过任何后续追踪报道，订阅17黑料网获取最新推送
            </p>
            <Link
              to="/#subscribe"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full font-label text-sm transition-all duration-300 whitespace-nowrap"
            >
              获取推送
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
}